import { useState, useCallback } from "react";
import { useTheme } from "next-themes";
import Editor from "@monaco-editor/react";
import { Download } from "lucide-react";
import {
  CodeBlockCopyButton,
  StreamingIndicator,
} from "./SkillCodeBlockParts";

interface CustomRendererProps {
  code: string;
  isIncomplete?: boolean;
  language: string;
  meta?: string;
}

const SUPPORTED = ["csv", "tsv", "json", "yaml", "yml", "xml", "data"];

interface Opts {
  isStreaming: boolean;
}

function makeDataRenderer(opts: Opts) {
  return function SkillDataBlock({ code, language, meta }: CustomRendererProps) {
    const { resolvedTheme } = useTheme();
    const [copied, setCopied] = useState(false);
    const codeStr = code.replace(/\n$/, "");
    const lineCount = codeStr.split("\n").length;
    const filename = meta?.match(/(?:filename|name)=([^\s]+)/)?.[1];
    const editorHeight = Math.min(lineCount * 19 + 16, 420);

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codeStr);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch { /* blocked */ }
    }, [codeStr]);

    const handleDownload = useCallback(() => {
      const blob = new Blob([codeStr], { type: contentTypeFor(language) });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `data.${language === "yml" ? "yaml" : language}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, [codeStr, filename, language]);

    return (
      <div className="my-4 group relative">
        <div className="rounded-lg border border-border/60 overflow-hidden bg-card">
          <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border/50 bg-muted/40">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {language}
              </span>
              {filename ? (
                <span className="max-w-[180px] truncate text-xs font-medium text-foreground/70">
                  {filename}
                </span>
              ) : null}
              <span className="whitespace-nowrap text-xs text-muted-foreground/80">
                {lineCount} lines
              </span>
              <StreamingIndicator active={opts.isStreaming} />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <CodeBlockCopyButton show={true} isCopied={copied} onCopy={handleCopy} />
              <button
                type="button"
                title="Download"
                onClick={handleDownload}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Download</span>
              </button>
            </div>
          </div>

          <Editor
            height={`${editorHeight}px`}
            language={monacoLanguage(language)}
            value={codeStr}
            theme={resolvedTheme === "dark" ? "vs-dark" : "vs"}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 13,
              lineNumbers: "on",
              wordWrap: "on",
              renderLineHighlight: "none",
            }}
          />
        </div>
      </div>
    );
  };
}

function monacoLanguage(lang: string): string {
  if (lang === "yml") return "yaml";
  if (lang === "data") return "json";
  return lang;
}

function contentTypeFor(lang: string): string {
  switch (lang) {
    case "csv": return "text/csv";
    case "tsv": return "text/tab-separated-values";
    case "json": return "application/json";
    case "yaml": case "yml": return "application/yaml";
    case "xml": return "application/xml";
    default: return "text/plain";
  }
}

export function buildSkillDataRenderer(opts: Opts) {
  return {
    language: SUPPORTED,
    component: makeDataRenderer(opts),
  };
}
